import { useState, useEffect, useRef, FormEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import {
  VStack,
  Text,
  Button,
  PinInput,
  HStack,
  Field,
  useToast
} from "@/storybook-components";
import { useAppSelector } from "@/redux/store/hooks";
import { signinUser } from "@/helpers/signin-user";
import { isIosDevice } from "@/helpers/is-ios-device";

/** Extra time before full reload so the success toast can paint (iOS navigates aggressively). */
const IOS_OTP_SUCCESS_RELOAD_DELAY_MS = 350;

interface OtpLoginProps {
  onBack: () => void;
  extraQueryParams?: Record<string, any>;
  /** When true (e.g. guest-login embed in iframe), reload the parent window after success instead of this document. */
  reloadParentOnSuccess?: boolean;
}

const OTP_LENGTH = 6;

const OtpLogin = ({
  onBack,
  extraQueryParams,
  reloadParentOnSuccess = false
}: OtpLoginProps) => {
  const { translate } = useTranslator();
  const toast = useToast();
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const answers = useAppSelector((state) => state.questions.answers);
  const loginWindowRef = useRef<any>(null);
  const checkIntervalRef = useRef<number | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (checkIntervalRef.current) {
        window.clearInterval(checkIntervalRef.current);
      }
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      if (loginWindowRef.current && loginWindowRef.current.parentNode) {
        document.body.removeChild(loginWindowRef.current);
      }
    };
  }, []);

  const handleComplete = (value: string) => {
    setOtp(value);
    setError("");
  };

  const reloadWithParams = () => {
    if (extraQueryParams && Object.keys(extraQueryParams).length > 0) {
      const currentUrl = new window.URL(window.location.href);

      Object.entries(extraQueryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          currentUrl.searchParams.set(key, String(value));
        }
      });

      window.location.href = currentUrl.toString();
    } else {
      window.location.reload();
    }
  };

  const performReloadAfterOtpSuccess = () => {
    if (reloadParentOnSuccess && window.parent !== window.self) {
      try {
        window.parent.location.reload();
      } catch {
        reloadWithParams();
      }
    } else {
      reloadWithParams();
    }
  };

  const handleLoginViaOtp = (e: FormEvent) => {
    e.preventDefault();
    const email = answers["email"] || "";

    setIsSubmitting(true);

    // Create an invisible iframe
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.name = "login-iframe";
    document.body.appendChild(iframe);

    loginWindowRef.current = iframe;

    // Use the signinUser helper to submit form in the iframe
    signinUser(email, undefined, "1", otp, iframe.contentWindow);

    // Poll to check the result
    checkIntervalRef.current = window.setInterval(() => {
      try {
        // Check if we can access the iframe's location (same origin)
        const iframeUrl = iframe.contentWindow?.location.href;

        if (iframeUrl && iframeUrl !== "about:blank") {
          const urlLower = iframeUrl.toLowerCase();

          if (
            urlLower.includes("error") ||
            urlLower.includes("invalid") ||
            urlLower.includes("users/session")
          ) {
            // Login failed
            window.clearInterval(checkIntervalRef.current!);
            if (timeoutRef.current) {
              window.clearTimeout(timeoutRef.current);
            }
            document.body.removeChild(iframe);
            setError(translate("common.invalidOtp"));
            setIsSubmitting(false);
          } else {
            // Login successful
            window.clearInterval(checkIntervalRef.current!);
            if (timeoutRef.current) {
              window.clearTimeout(timeoutRef.current);
            }
            document.body.removeChild(iframe);
            toast({
              title: translate("profileGate.youAreNowLoggedIn"),
              status: "success",
              duration: 3000,
              isClosable: true
            });
            if (isIosDevice()) {
              window.setTimeout(
                performReloadAfterOtpSuccess,
                IOS_OTP_SUCCESS_RELOAD_DELAY_MS
              );
            } else {
              performReloadAfterOtpSuccess();
            }
          }
        }
      } catch {
        // Cross-origin error - continue polling
      }
    }, 500);

    // Timeout after 15 minutes
    timeoutRef.current = window.setTimeout(() => {
      if (checkIntervalRef.current) {
        window.clearInterval(checkIntervalRef.current);
        if (iframe.parentNode) {
          document.body.removeChild(iframe);
        }
        setIsSubmitting(false);
        onBack();
        toast({
          title: translate("common.sentenceCasedError"),
          description: translate("common.loginTimeout"),
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }, 900000);
  };

  return (
    <VStack
      as="form"
      noValidate
      gap={4}
      align="stretch"
      onSubmit={handleLoginViaOtp}
    >
      <Text fontSize="sm" color="gray.600" textAlign="center">
        {translate("profileGate.didntReceiveCode")} <br />
        {translate("profileGate.checkSpamAndJunkFolders")}
      </Text>

      <Field.Root invalid={!!error} required>
        <Field.Label textAlign="center">
          {translate("profileGate.sixDigitCode")}
        </Field.Label>
        <HStack justify="center">
          <PinInput
            otp
            size="lg"
            count={OTP_LENGTH}
            value={otp}
            onChange={setOtp}
            onComplete={handleComplete}
            disabled={isSubmitting}
            invalid={!!error}
            getInputProps={(index) => ({
              "data-testid": `guest-login-otp-input-${index + 1}`
            })}
          />
        </HStack>
        {!!error && (
          <Field.ErrorText justifyContent="center">{error}</Field.ErrorText>
        )}
      </Field.Root>

      <VStack gap={3} width="100%">
        <Button
          width="100%"
          variant="primary"
          disabled={otp.length !== OTP_LENGTH || isSubmitting}
          loading={isSubmitting}
          loadingText={translate("common.login")}
          type="submit"
          data-testid="guest-login-otp-submit-button"
        >
          {translate("common.login")}
        </Button>

        <Button
          width="100%"
          variant="secondary"
          type="reset"
          onClick={onBack}
          disabled={isSubmitting}
          data-testid="guest-login-otp-back-button"
        >
          {translate("common.sentenceCasedBack")}
        </Button>
      </VStack>
    </VStack>
  );
};

export default OtpLogin;
